import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { appointmentsService, reportsService } from '../services/api';
import { formatMoney } from '../utils/pricing';
import { AppointmentModal } from '../components/AppointmentModal';
import { WebsiteVisits } from '../components/WebsiteVisits';
import { formatApptRange, formatApptDateLong } from '../utils/dateFormatters';
import './Dashboard.css';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [pl, setPl] = useState(null); // this month's P&L totals (GID-Session-016)

  async function load() {
    setLoading(true);
    const res = await appointmentsService.getDashboard();
    if (res.success) setData(res.dashboard);
    else setError(res.error);
    setLoading(false);
    reportsService.pl({ preset: 'this_month' }).then((r) => { if (r.success) setPl(r.report.totals); });
  }
  useEffect(() => { load(); }, []);

  async function confirm(id) {
    await appointmentsService.updateStatus(id, 'confirmed');
    load();
  }

  if (loading) return <div className="dashboard-loading"><div className="loading-spinner"></div><p>Loading…</p></div>;
  if (error) return <div className="dashboard-error"><p>{error}</p><button onClick={load} className="btn btn-primary">Retry</button></div>;

  const { today = [], upcoming = [], pending = [], stats = {} } = data || {};

  const Item = ({ a, showDate, action }) => (
    <div className="job-card" onClick={() => setModal({ appointment: a })} style={{ cursor: 'pointer' }}>
      <div className="job-time">{showDate ? formatApptDateLong(a.starts_at) : formatApptRange(a.starts_at, a.ends_at)}</div>
      <div className="job-info">
        <h3>{a.service_name || 'Appointment'}</h3>
        <p className="job-customer">{a.client_name || '—'}{(a.crew_names || a.staff_name) ? ` · ${a.crew_names || a.staff_name}` : ''}</p>
      </div>
      {action || <span className={`status-badge status-${a.status.replace('_', '-')}`}>{a.status.replace('_', ' ')}</span>}
    </div>
  );

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <button className="btn btn-primary" onClick={() => setModal({ create: true })}>+ New Appointment</button>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><span className="stat-value">{stats.today || 0}</span><span className="stat-label">Today</span></div>
        <div className="stat-card"><span className="stat-value">{stats.week || 0}</span><span className="stat-label">This Week</span></div>
        <div className="stat-card"><span className="stat-value">{stats.pending || 0}</span><span className="stat-label">Pending Confirmation</span></div>
        <Link to="/quotes" className="stat-card stat-link"><span className="stat-value">{(stats.quotes_sent || 0) + (stats.quotes_to_book || 0)}</span><span className="stat-label">Quotes Needing Attention</span></Link>
      </div>

      {pl && (
        <div className="stats-grid dash-pl" title="Jobs done this month — revenue, their direct costs, the overheads they carry, and what is left. Full picture under Reports.">
          <Link to="/reports" className="stat-card stat-link"><span className="stat-value">{formatMoney(pl.revenue)}</span><span className="stat-label">Revenue this month</span></Link>
          <Link to="/reports" className="stat-card stat-link"><span className="stat-value">{formatMoney(pl.direct_costs)}</span><span className="stat-label">Job costs this month</span></Link>
          <Link to="/reports" className="stat-card stat-link"><span className="stat-value">{formatMoney(pl.overheads)}</span><span className="stat-label">Overheads this month</span></Link>
          <Link to="/reports" className={`stat-card stat-link ${pl.profit < 0 ? 'dash-neg' : 'dash-pos'}`}><span className="stat-value">{pl.profit < 0 ? '-' : ''}{formatMoney(Math.abs(pl.profit))}</span><span className="stat-label">Profit this month · {pl.jobs} job{pl.jobs === 1 ? '' : 's'}</span></Link>
        </div>
      )}

      <WebsiteVisits />

      <div className="dashboard-grid">
        <div className="dashboard-section">
          <div className="section-header"><h2>Today's Appointments</h2><span className="job-count">{today.length}</span></div>
          {today.length === 0 ? <div className="empty-state"><p>Nothing scheduled today</p></div>
            : <div className="job-list">{today.map((a) => <Item key={a.id} a={a} />)}</div>}
        </div>

        <div className="dashboard-section">
          <div className="section-header"><h2>Upcoming This Week</h2><span className="job-count">{upcoming.length}</span></div>
          {upcoming.length === 0 ? <div className="empty-state"><p>No upcoming appointments</p></div>
            : <div className="job-list">{upcoming.slice(0, 6).map((a) => <Item key={a.id} a={a} showDate />)}</div>}
        </div>

        <div className="dashboard-section attention-section">
          <div className="section-header"><h2>Pending Confirmation</h2><span className="job-count warning">{pending.length}</span></div>
          {pending.length === 0 ? <div className="empty-state success"><p>All caught up!</p></div>
            : <div className="job-list">{pending.map((a) => (
                <Item key={a.id} a={a} showDate action={
                  <button className="btn btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); confirm(a.id); }}>Confirm</button>
                } />
              ))}</div>}
        </div>
      </div>

      <AppointmentModal
        isOpen={!!modal}
        onClose={() => setModal(null)}
        onSave={() => { setModal(null); load(); }}
        appointment={modal?.appointment || null}
      />
    </div>
  );
}
